/**
 * Extrai erros de validação no formato Zod `.flatten()` (e estruturas compatíveis)
 * retornadas pelo backend (ex.: vover-billing `errors: parsed.error.flatten()`).
 *
 * Padrão alinhado ao vover-app: exibir `helperText` + `error` em cada TextField.
 */

export interface ZodFlattenedErrors {
  formErrors: string[];
  fieldErrors: Record<string, string[] | undefined>;
}

export interface ParsedApiValidation {
  fieldErrors: Record<string, string>;
  formErrorMessages: string[];
  summaryMessage: string | null;
  hasFieldValidation: boolean;
}

function joinMessages(messages: string[]): string {
  return messages.filter(m => typeof m === 'string' && m.trim()).join(' · ');
}

/**
 * Interpreta o corpo de erro de uma resposta Axios (ou similar).
 */
export function parseApiValidationErrors(error: unknown): ParsedApiValidation {
  const empty: ParsedApiValidation = {
    fieldErrors: {},
    formErrorMessages: [],
    summaryMessage: null,
    hasFieldValidation: false
  };

  const err = error as { response?: { data?: unknown } };
  const data = err.response?.data as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object') return empty;

  const detailFromBody =
    typeof data.detail === 'string' && data.detail.trim() ? data.detail.trim() : null;

  const rawMessage =
    typeof data.message === 'string' && data.message.trim() ? data.message.trim() : null;

  const rawErrors = data.errors;
  if (!rawErrors || typeof rawErrors !== 'object') {
    const summaryMessage = detailFromBody || rawMessage;
    return { ...empty, summaryMessage };
  }

  const flattened = rawErrors as Partial<ZodFlattenedErrors>;
  const fieldMap = flattened.fieldErrors;

  if (fieldMap && typeof fieldMap === 'object') {
    const fieldErrors: Record<string, string> = {};
    for (const [key, messages] of Object.entries(fieldMap)) {
      if (!Array.isArray(messages) || messages.length === 0) continue;
      const text = joinMessages(messages as string[]);
      if (text) fieldErrors[key] = text;
    }

    const formErrorMessages = Array.isArray(flattened.formErrors)
      ? (flattened.formErrors as string[]).filter(m => typeof m === 'string' && m.trim())
      : [];

    const joinedFromFlatten = [
      ...formErrorMessages,
      ...Object.entries(fieldErrors).map(([k, v]) => `${k}: ${v}`)
    ].join(' · ');

    const summaryMessage =
      detailFromBody ||
      (joinedFromFlatten.trim() ? joinedFromFlatten : null) ||
      (rawMessage && rawMessage !== 'Validation error' ? rawMessage : null);

    return {
      fieldErrors,
      formErrorMessages,
      summaryMessage,
      hasFieldValidation: Object.keys(fieldErrors).length > 0 || formErrorMessages.length > 0
    };
  }

  return { ...empty, summaryMessage: detailFromBody || rawMessage };
}
